import { useRef, useState, useEffect } from "react";
import {
  FaPhoneAlt,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaArrowUp,
} from "react-icons/fa";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import Noise from "../core/noise";
import FitWidthText from "./hero-text";

const footerLinks = [
  {
    title: "SIGNAGE SOLUTIONS",
    links: [
      { name: "Aluminum Signs", href: "#" },
      { name: "Exterior Signs", href: "#" },
      { name: "Interior Signs", href: "#" },
      { name: "Metal Signs", href: "#" },
      { name: "Pylon Signs", href: "#" },
      { name: "Illuminated Signs", href: "#" },
      { name: "Fascia Signs", href: "#" },
      { name: "Office Signs", href: "#" },
      { name: "Custom Signs", href: "#" },
    ],
  },
  {
    title: "TECHNICAL SERVICES",
    links: [
      { name: "Indoor/Outdoor LED", href: "#" },
      { name: "Message Boards", href: "#" },
      { name: "Wholesale Solutions", href: "#" },
      { name: "Sign Service Install", href: "#" },
      { name: "Bucket Truck Services", href: "#" },
    ],
  },
  {
    title: "PROJECT GALLERY",
    links: [
      { name: "Letter Signs", href: "#" },
      { name: "Exterior Portfolios", href: "#" },
      { name: "Freestanding Pylon", href: "#" },
      { name: "Fascia Showcase", href: "#" },
      { name: "Interior Gallery", href: "#" },
    ],
  },
  {
    title: "CORPORATE HUB",
    links: [
      { name: "Business Owners", href: "#" },
      { name: "Marketing Pros", href: "#" },
      { name: "Property Managers", href: "#" },
      { name: "Areas We Serve", href: "#" },
      { name: "Edmonton Services", href: "#" },
      { name: "Calgary Services", href: "#" },
    ],
  },
];

const Footer = () => {
  const footerRef = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkSize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  useGSAP(
    () => {
      const cardEl = cardRef.current;
      const triggerEl = triggerRef.current;
      if (!cardEl || !triggerEl) return;
      gsap.to(cardEl, {
        y: isMobile ? -350 : -60,
        scrollTrigger: {
          trigger: triggerEl,
          scrub: 1,
          start: "top bottom+=-40%",
          end: "+=100%",
          invalidateOnRefresh: true,
        },
      });
    },
    { scope: footerRef, dependencies: [isMobile] },
  );

  return (
    <footer
      ref={footerRef}
      className="w-full z-2 text-white flex flex-col relative overflow-hidden pb-20"
    >
      <div
        ref={cardRef}
        className="translate-y-40 mx-auto w-full px-2.5 md:px-6 relative z-10"
      >
        <div className="w-full bg-white/5 backdrop-blur-3xl border border-white/10 p-10 md:p-16 rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] relative overflow-hidden">
          <Noise />
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-10 mb-20 relative z-10">
            <div className="flex items-center gap-8">
              <div className="flex flex-col leading-none">
                <span className="text-4xl font-black tracking-tighter text-white">
                  METAL<span className="text-[#4991b8] italic">FX</span>
                </span>
                <span className="text-[10px] font-bold tracking-[0.4em] text-zinc-600 uppercase mt-1">
                  Industrial Identity
                </span>
              </div>
              <div className="w-px h-12 bg-white/10 hidden sm:block"></div>
              <div className="flex items-center gap-4 text-[#4991b8]">
                <div className="w-9 md:w-10 aspect-square rounded-full border border-[#4991b8]/50 flex items-center justify-center animate-pulse">
                  <FaPhoneAlt size={14} />
                </div>
                <span className="text-xl md:text-2xl font-black uppercase tracking-tighter text-white/90">
                  NEED HELP?
                </span>
              </div>
            </div>
            <div className="flex flex-col md:items-end">
              <span className="text-[10px] font-black text-zinc-500 tracking-[0.2em] mb-1 uppercase">
                Direct Response Unit
              </span>
              <a
                href="tel:18556382539"
                className="text-3xl md:text-5xl lg:text-6xl font-black text-[#4991b8] tracking-tighter hover:text-white transition-all transform hover:scale-105 origin-right"
              >
                <span className="text-white/20 mr-2 text-2xl font-medium sm:text-3xl">
                  CALL
                </span>
                1.855.638.2539
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-16 relative z-10">
            <div className="hidden lg:flex flex-col justify-between items-start pr-12 border-r border-white/5">
              <div className="flex flex-col gap-6">
                <p className="text-zinc-500 text-sm font-medium leading-relaxed">
                  Canadian-made industrial identifiers engineered for permanence
                  and brand dominance.
                </p>
                <button
                  onClick={scrollToTop}
                  className="flex items-center gap-3 group text-[#4991b8] font-bold uppercase text-[10px] tracking-widest mt-4"
                >
                  <span className="w-8 h-8 rounded-full border border-[#4991b8]/20 flex items-center justify-center group-hover:bg-[#4991b8] group-hover:text-black transition-all">
                    <FaArrowUp size={12} />
                  </span>
                  Scroll To Top
                </button>
              </div>
              <div className="w-16 h-16 opacity-10">
                <svg viewBox="0 0 100 100" fill="currentColor">
                  <path d="M0 0h100v100H0z" />
                </svg>
              </div>
            </div>
            {footerLinks.map((section, idx) => (
              <div key={idx} className="flex flex-col gap-10">
                <div className="flex items-baseline gap-2">
                  <div className="w-1.5 h-1.5 bg-[#4991b8] rounded-full"></div>
                  <h5 className="font-black text-[9px] tracking-[0.4em] text-zinc-500 uppercase">
                    {section.title}
                  </h5>
                </div>
                <ul className="flex flex-col gap-5">
                  {section.links.map((link, lIdx) => (
                    <li key={lIdx}>
                      <a
                        href={link.href}
                        className="text-zinc-400 hover:text-[#4991b8] text-[14px] font-semibold tracking-tight transition-all flex items-center gap-0 hover:gap-3 group"
                      >
                        <span className="w-0 h-px bg-[#4991b8] group-hover:w-4 transition-all duration-500"></span>
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-24 pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-end md:items-center gap-12 relative z-10">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
              <div className="flex gap-4">
                {[FaFacebookF, FaInstagram, FaLinkedinIn].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white hover:text-black transition-all border border-white/5"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
              <div className="w-px h-10 bg-white/10 hidden md:block"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 text-[13px] font-bold tracking-tight text-zinc-500">
                <div className="flex flex-col pr-8 border-r border-white/5">
                  <span className="text-white/40 uppercase text-[10px] font-black tracking-widest mb-2">
                    Edmonton HQ
                  </span>
                  <p className="uppercase leading-relaxed">
                    16115 72 Street // AB T5Z 3T7
                  </p>
                </div>
                <div className="flex flex-col">
                  <span className="text-white/40 uppercase text-[10px] font-black tracking-widest mb-2">
                    Calgary Hub
                  </span>
                  <p className="uppercase leading-relaxed">
                    15 Covemeadow Close // AB T3K 6B2
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:items-end gap-3 text-right">
              <p className="text-white font-black text-[11px] uppercase tracking-[0.3em] leading-none mb-1">
                © {new Date().getFullYear()} /METAL FX SIGNS & METALS LTD.
              </p>
              <div className="flex gap-10 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
                <a href="#" className="hover:text-[#4991b8] transition-colors">
                  Privacy & Compliance
                </a>
                <a href="#" className="hover:text-[#4991b8] transition-colors">
                  Industrial Legal
                </a>
              </div>
            </div>
          </div>{" "}
          <div className="absolute -bottom-10 -left-10 text-white/5 pointer-events-none select-none">
            <FaArrowUp size={300} className="rotate-45" />
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full" ref={triggerRef}>
        <div className="w-full relative -mt-20 px-2.5 md:px-6 z-0 pointer-events-none">
          <FitWidthText className="font-black select-none pointer-events-none opacity-[0.7]">
            <span className="flex items-center gap-[0.01em]">
              <span className="text-white">METAL&nbsp;</span>
              <span className="text-[#4991b8] italic">FX</span>
            </span>
          </FitWidthText>
        </div>
        <div className=" mx-auto w-full px-6 pt-24 relative z-10 flex flex-col md:flex-row justify-between items-start border-t border-white/5 gap-12 text-white">
          <div className="flex flex-col gap-5 max-w-sm">
            <h6 className="text-[10px] font-black uppercase tracking-[0.6em] text-white/30">
              Manufacturing the Future
            </h6>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] leading-relaxed text-zinc-600">
              Every identifier produced by Metal FX is a testament to our
              commitment to durability, precision, and the power of physical
              presence. We forge what others only imagine.
            </p>
          </div>
          <div className="flex flex-col md:items-end gap-10 text-center md:text-right">
            <div className="flex flex-col md:items-end gap-3">
              <span className="text-[10px] font-black uppercase tracking-[0.6em] text-white/30">
                Industrial Protocol
              </span>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-600">
                ISO 9001:2015 Compliant Production // LEED v4 Materials
              </p>
            </div>
            <div className="flex flex-col md:items-end gap-3 pt-4 border-t border-white/5 md:border-t-0">
              <p className="text-[10px] font-black uppercase tracking-[0.6em] text-zinc-800">
                © {new Date().getFullYear()} / METAL FX SIGNS & METALS LTD.
              </p>
              <div className="flex gap-10 font-black text-[9px] uppercase tracking-[0.5em] text-zinc-800">
                <span className="hover:text-[#4991b8] transition-colors cursor-help">
                  FX-PRM-CODE-2026
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
